import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CircleHelp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { settingsApi, SystemSetting } from '@/api/settings';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';

interface RateLimitSettingsPanelProps {
  settings: SystemSetting[];
  canManage: boolean;
}

interface RateLimitRowDefinition {
  id: string;
  label: string;
  windowLabel: string;
  extKey: string;
  intKey: string;
  defaultExt: number;
  defaultInt: number;
  helpTitle: string;
  helpLines: string[];
}

const NETWORK_UNACH_CIDR_KEY = 'NETWORK_UNACH_CIDR';

const RATE_LIMIT_ROWS: RateLimitRowDefinition[] = [
  {
    id: 'curp',
    label: 'CURP',
    windowLabel: 'Se reinicia cada 15 minutos',
    extKey: 'RATE_LIMIT_CURP_MAX_EXT',
    intKey: 'RATE_LIMIT_CURP_MAX_INT',
    defaultExt: 10500,
    defaultInt: 10500,
    helpTitle: 'Validacion CURP',
    helpLines: [
      'Protege la validacion de CURP en el registro publico.',
      'Si queda muy bajo, usuarios legitimos pueden bloquearse en redes compartidas.',
      'Si se sube demasiado, aumenta la exposicion a abuso automatizado.',
    ],
  },
  {
    id: 'siae',
    label: 'SIAE',
    windowLabel: 'Se reinicia cada 5 minutos',
    extKey: 'RATE_LIMIT_SIAE_MAX_EXT',
    intKey: 'RATE_LIMIT_SIAE_MAX_INT',
    defaultExt: 15000,
    defaultInt: 15000,
    helpTitle: 'Validacion SIAE',
    helpLines: [
      'Protege la validacion de matricula/SIAE.',
      'Un valor muy bajo puede bloquear registros reales en laboratorios o redes institucionales.',
      'Un valor muy alto puede aumentar consultas automaticas al servicio externo.',
    ],
  },
  {
    id: 'login',
    label: 'Login / submit',
    windowLabel: 'Se reinicia cada 15 minutos',
    extKey: 'RATE_LIMIT_LOGIN_MAX_EXT',
    intKey: 'RATE_LIMIT_LOGIN_MAX_INT',
    defaultExt: 30000,
    defaultInt: 30000,
    helpTitle: 'Login / submit',
    helpLines: [
      'Protege el acceso administrativo y el envio final del registro.',
      'Valores bajos pueden bloquear operacion legitima durante aperturas masivas.',
      'Valores muy altos reducen la contencion frente a intentos masivos.',
    ],
  },
  {
    id: 'tracking',
    label: 'Tracking',
    windowLabel: 'Se reinicia cada 15 minutos',
    extKey: 'RATE_LIMIT_TRACKING_MAX_EXT',
    intKey: 'RATE_LIMIT_TRACKING_MAX_INT',
    defaultExt: 10000,
    defaultInt: 10000,
    helpTitle: 'Tracking',
    helpLines: [
      'Protege la consulta de folios y seguimiento.',
      'Valores bajos afectan staff o grupos que consultan varios folios.',
      'Valores muy altos permiten abuso de consultas automatizadas.',
    ],
  },
  {
    id: 'resend',
    label: 'Resend',
    windowLabel: 'Se reinicia cada 30 minutos',
    extKey: 'RATE_LIMIT_RESEND_MAX_EXT',
    intKey: 'RATE_LIMIT_RESEND_MAX_INT',
    defaultExt: 200,
    defaultInt: 200,
    helpTitle: 'Resend',
    helpLines: [
      'Protege el reenvio de correos y credenciales.',
      'Si se sube demasiado, aumenta el riesgo de abuso del servicio de correo.',
      'Mantenga un valor interno razonable incluso dentro de la red UNACH.',
    ],
  },
];

function buildVirtualSetting(
  key: string,
  defaultValue: number | string,
  type: SystemSetting['type'],
  description: string
): SystemSetting {
  return {
    id: `virtual-${key}`,
    scope: 'GLOBAL',
    editionId: null,
    eventId: null,
    key,
    value: defaultValue,
    type,
    description,
    updatedBy: 'system',
    updatedAt: new Date().toISOString(),
  };
}

function getSettingMap(settings: SystemSetting[]): Map<string, SystemSetting> {
  return new Map(settings.map((setting) => [setting.key, setting]));
}

function getNumericDraft(setting: SystemSetting | undefined, fallback: number): string {
  const numericValue = Number(setting?.value);
  return Number.isFinite(numericValue) ? String(numericValue) : String(fallback);
}

export function RateLimitSettingsPanel({ settings, canManage }: RateLimitSettingsPanelProps) {
  const queryClient = useQueryClient();
  const settingsMap = useMemo(() => getSettingMap(settings), [settings]);

  const [networkValue, setNetworkValue] = useState<string>('');
  const [rowDrafts, setRowDrafts] = useState<Record<string, { ext: string; int: string }>>({});

  useEffect(() => {
    const nextDrafts: Record<string, { ext: string; int: string }> = {};
    for (const row of RATE_LIMIT_ROWS) {
      nextDrafts[row.id] = {
        ext: getNumericDraft(settingsMap.get(row.extKey), row.defaultExt),
        int: getNumericDraft(settingsMap.get(row.intKey), row.defaultInt),
      };
    }

    const existingNetwork = settingsMap.get(NETWORK_UNACH_CIDR_KEY);
    setNetworkValue(typeof existingNetwork?.value === 'string' ? existingNetwork.value : '');
    setRowDrafts(nextDrafts);
  }, [settingsMap]);

  const networkSetting =
    settingsMap.get(NETWORK_UNACH_CIDR_KEY) ??
    buildVirtualSetting(
      NETWORK_UNACH_CIDR_KEY,
      '',
      'STRING',
      'IPs o rangos CIDR institucionales separados por comas.'
    );

  const networkMutation = useMutation({
    mutationFn: async (value: string) =>
      settingsApi.updateSetting(NETWORK_UNACH_CIDR_KEY, {
        scope: 'GLOBAL',
        editionId: null,
        eventId: null,
        value,
        type: 'STRING',
        description: networkSetting.description ?? undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('CIDRs institucionales actualizados.');
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Error desconocido';
      toast.error(`No fue posible guardar los CIDRs: ${message}`);
    },
  });

  const rowMutation = useMutation({
    mutationFn: async ({ rowId, extValue, intValue }: { rowId: string; extValue: number; intValue: number }) => {
      const row = RATE_LIMIT_ROWS.find((candidate) => candidate.id === rowId);
      if (!row) {
        throw new Error('Fila de rate limit no encontrada.');
      }

      const extSetting =
        settingsMap.get(row.extKey) ??
        buildVirtualSetting(row.extKey, row.defaultExt, 'NUMBER', `${row.label} externo. ${row.windowLabel}.`);
      const intSetting =
        settingsMap.get(row.intKey) ??
        buildVirtualSetting(row.intKey, row.defaultInt, 'NUMBER', `${row.label} interno UNACH. ${row.windowLabel}.`);

      await settingsApi.updateSetting(row.extKey, {
        scope: 'GLOBAL',
        editionId: null,
        eventId: null,
        value: extValue,
        type: 'NUMBER',
        description: extSetting.description ?? undefined,
      });

      await settingsApi.updateSetting(row.intKey, {
        scope: 'GLOBAL',
        editionId: null,
        eventId: null,
        value: intValue,
        type: 'NUMBER',
        description: intSetting.description ?? undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Rate limits operativos actualizados.');
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Error desconocido';
      toast.error(`No fue posible guardar la fila: ${message}`);
    },
  });

  const handleRowDraftChange = (rowId: string, field: 'ext' | 'int', value: string) => {
    setRowDrafts((current) => ({
      ...current,
      [rowId]: {
        ...current[rowId],
        [field]: value,
      },
    }));
  };

  const handleSaveNetwork = () => {
    networkMutation.mutate(networkValue.trim());
  };

  const handleSaveRow = (row: RateLimitRowDefinition) => {
    const draft = rowDrafts[row.id];
    const extValue = Number(draft?.ext);
    const intValue = Number(draft?.int);
    const minimum = row.id === 'resend' ? 3 : 5;
    const maximum = row.id === 'resend' ? 1000 : 30000;

    if (!Number.isInteger(extValue) || !Number.isInteger(intValue)) {
      toast.error('Los valores Externo e Interno deben ser numeros enteros.');
      return;
    }

    if (extValue < minimum || extValue > maximum || intValue < minimum || intValue > maximum) {
      toast.error(`Los valores deben permanecer entre ${minimum} y ${maximum}.`);
      return;
    }

    rowMutation.mutate({ rowId: row.id, extValue, intValue });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-blue-900">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-1 text-sm font-medium">
            <p>Los cambios pueden tardar hasta 60 segundos en reflejarse en los limitadores del servidor.</p>
            <p>Si no se captura ningun CIDR institucional, todo el trafico se tratara como Externo.</p>
          </div>
        </div>
      </div>

      <Card className="rounded-xl border-gray-200 shadow-sm">
        <CardHeader className="space-y-2">
          <CardTitle className="text-base font-semibold text-slate-900">Rangos institucionales UNACH</CardTitle>
          <div className="space-y-1 text-sm text-slate-600">
            <p>Dejar en blanco para aplicar restricciones externas a todo el trafico.</p>
            <p>Ingrese IPs o rangos CIDR separados por comas. Ejemplo: 148.228.0.0/16, 200.23.45.0/24, 148.228.10.15</p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={networkValue}
            onChange={(event) => setNetworkValue(event.target.value)}
            rows={4}
            placeholder="148.228.0.0/16, 200.23.45.0/24"
            disabled={!canManage || networkMutation.isPending}
          />
          <div className="flex justify-end">
            <Button
              className="h-10 rounded-lg bg-[#1e3a5f] text-white hover:bg-[#18314f]"
              onClick={handleSaveNetwork}
              disabled={!canManage || networkMutation.isPending}
            >
              {networkMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                'Guardar CIDRs'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border-gray-200 shadow-sm">
        <CardHeader className="space-y-2">
          <CardTitle className="text-base font-semibold text-slate-900">Rate Limits operativos por perfil de red</CardTitle>
          <p className="text-sm text-slate-600">
            Edite solo el valor maximo. La ventana fija se mantiene en codigo y no cambia desde esta pantalla.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="hidden grid-cols-[minmax(220px,1.5fr)_140px_140px_120px] gap-3 border-b border-slate-200 px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
            <div>Limitador</div>
            <div>Externo</div>
            <div>Interno UNACH</div>
            <div className="text-right">Accion</div>
          </div>

          <div className="space-y-3">
            {RATE_LIMIT_ROWS.map((row) => {
              const draft = rowDrafts[row.id] ?? {
                ext: String(row.defaultExt),
                int: String(row.defaultInt),
              };

              return (
                <div
                  key={row.id}
                  className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-[minmax(220px,1.5fr)_140px_140px_120px] md:items-center"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">{row.label}</p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
                            aria-label={`Ayuda sobre ${row.label}`}
                          >
                            <CircleHelp className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          sideOffset={8}
                          className="z-50 max-w-[280px] rounded-md bg-slate-800 px-3 py-2 text-left text-xs leading-relaxed text-white shadow-lg"
                        >
                          <div className="space-y-2">
                            <p className="font-semibold">{row.helpTitle}</p>
                            {row.helpLines.map((line) => (
                              <p key={line}>{line}</p>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <p className="text-xs text-slate-500">{row.windowLabel}</p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs font-medium text-slate-500 md:hidden">Externo</p>
                    <Input
                      type="number"
                      min={row.id === 'resend' ? 3 : 5}
                      step={1}
                      value={draft.ext}
                      onChange={(event) => handleRowDraftChange(row.id, 'ext', event.target.value)}
                      disabled={!canManage || rowMutation.isPending}
                    />
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs font-medium text-slate-500 md:hidden">Interno UNACH</p>
                    <Input
                      type="number"
                      min={row.id === 'resend' ? 3 : 5}
                      step={1}
                      value={draft.int}
                      onChange={(event) => handleRowDraftChange(row.id, 'int', event.target.value)}
                      disabled={!canManage || rowMutation.isPending}
                    />
                  </div>

                  <div className="flex md:justify-end">
                    <Button
                      className="h-10 w-full rounded-lg bg-[#1e3a5f] text-white hover:bg-[#18314f] md:w-auto"
                      onClick={() => handleSaveRow(row)}
                      disabled={!canManage || rowMutation.isPending}
                    >
                      {rowMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        'Guardar'
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
